package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.material3.*
import androidx.compose.material3.TabRowDefaults.tabIndicatorOffset
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.nestedscroll.NestedScrollConnection
import androidx.compose.ui.input.nestedscroll.NestedScrollSource
import androidx.compose.ui.input.nestedscroll.nestedScroll
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.DateTimeUtils
import co.uk.sacursoftware.ssinventory.util.LocalUserRole
import co.uk.sacursoftware.ssinventory.util.UserRole
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun StockScreen(
    initialPageIndex: Int = 0,
    onPageChange: (Int) -> Unit = {},
    onBack: () -> Unit,
    onNewCount: (Supplier) -> Unit,
    onAmendCount: (Supplier, StockCount) -> Unit,
    onShowHistory: (Supplier) -> Unit = {}
) {
    val scope = rememberCoroutineScope()
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }

    var suppliers by remember { mutableStateOf<List<Supplier>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        repository.getSuppliers().fold(
            onSuccess = {
                suppliers = it
                isLoading = false
            },
            onFailure = {
                error = it.message
                isLoading = false
            }
        )
    }

    Box(modifier = Modifier.fillMaxSize()) {
        Scaffold(
            topBar = {
                TopAppBar(
                    title = { ScreenTitle("Stock Levels") },
                    navigationIcon = {
                        IconButton(onClick = onBack) {
                            BackArrowIcon()
                        }
                    }
                )
            }
        ) { padding ->
            Box(modifier = Modifier.padding(padding).fillMaxSize()) {
                if (error != null) {
                    Text("Error: $error", modifier = Modifier.align(Alignment.Center), color = MaterialTheme.colorScheme.error)
                } else if (!isLoading && suppliers.isEmpty()) {
                    Text("No suppliers found.", modifier = Modifier.align(Alignment.Center))
                } else if (!isLoading) {
                    val pagerState = rememberPagerState(
                        initialPage = initialPageIndex.coerceIn(0, maxOf(0, suppliers.size - 1)),
                        pageCount = { suppliers.size }
                    )

                    LaunchedEffect(pagerState.currentPage) {
                        onPageChange(pagerState.currentPage)
                    }

                    Column {
                        ScrollableTabRow(
                            selectedTabIndex = pagerState.currentPage,
                            edgePadding = 16.dp,
                            containerColor = MaterialTheme.colorScheme.surface,
                            contentColor = MaterialTheme.colorScheme.primary,
                            divider = {},
                            indicator = { tabPositions ->
                                if (pagerState.currentPage < tabPositions.size) {
                                    TabRowDefaults.SecondaryIndicator(
                                        modifier = Modifier.tabIndicatorOffset(tabPositions[pagerState.currentPage]),
                                        color = MaterialTheme.colorScheme.secondary
                                    )
                                }
                            }
                        ) {
                            suppliers.forEachIndexed { index, supplier ->
                                Tab(
                                    selected = pagerState.currentPage == index,
                                    onClick = { scope.launch { pagerState.animateScrollToPage(index) } },
                                    text = { Text(supplier.name) }
                                )
                            }
                        }

                        HorizontalPager(
                            state = pagerState,
                            modifier = Modifier.fillMaxSize()
                        ) { page ->
                            SupplierStockView(
                                supplier = suppliers[page],
                                repository = repository
                            )
                        }
                    }
                }
            }
        }

        if (isLoading) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(MaterialTheme.colorScheme.background)
                    .clickable(enabled = false) { },
                contentAlignment = Alignment.Center
            ) {
                BrandLoadingSpinner(Modifier.fillMaxSize())
            }
        }
    }
}

@Composable
fun DraftChoiceDialog(
    supplierName: String,
    onContinue: () -> Unit,
    onStartNew: () -> Unit,
    onDismiss: () -> Unit
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Draft found") },
        text = { Text("An unfinished count for $supplierName was found. Would you like to continue or start a new one?") },
        confirmButton = {
            Button(onClick = onContinue, shape = MaterialTheme.shapes.medium) {
                Text("Continue")
            }
        },
        dismissButton = {
            TextButton(onClick = onStartNew) {
                Text("Start New", color = MaterialTheme.colorScheme.secondary)
            }
        },
        shape = MaterialTheme.shapes.large
    )
}

@Composable
fun SupplierStockView(
    supplier: Supplier,
    repository: SSInventoryRepository
) {
    val userRole = LocalUserRole.current
    var latestCount by remember(supplier.id) { mutableStateOf<StockCount?>(null) }
    var countDetails by remember(supplier.id) { mutableStateOf<List<StockCountItem>>(emptyList()) }
    var itemNames by remember(supplier.id) { mutableStateOf<Map<String, String>>(emptyMap()) }
    var itemsMap by remember(supplier.id) { mutableStateOf<Map<String, Item>>(emptyMap()) }
    var isLoading by remember(supplier.id) { mutableStateOf(true) }

    val totalStockValuation = remember(countDetails, itemsMap) {
        var total = 0.0
        countDetails.forEach { detail ->
            val item = itemsMap[detail.itemId]
            if (item != null) {
                total += calculateItemCost(item, detail.countedRealAmount)
            }
        }
        total
    }

    LaunchedEffect(supplier.id) {
        isLoading = true
        
        // Fetch items and counts in parallel or sequence
        val itemsResult = repository.getItems(supplier.id)
        if (itemsResult.isSuccess) {
            val list = itemsResult.getOrThrow()
            itemsMap = list.associateBy { it.id }
            itemNames = list.associate { it.id to it.name }
        }

        repository.getStockCounts(supplier.id).onSuccess { counts ->
            val count = counts.firstOrNull()
            latestCount = count
            if (count != null) {
                repository.getStockCountDetails(count.id).onSuccess {
                    countDetails = it
                }
            }
        }
        isLoading = false
    }

    Column(modifier = Modifier.fillMaxSize()) {
        val listState = rememberLazyListState()
        var collapse by remember(supplier.id) { mutableFloatStateOf(0f) }
        val nestedScroll = remember(listState) {
            object : NestedScrollConnection {
                override fun onPreScroll(available: Offset, source: NestedScrollSource): Offset {
                    if (available.y < 0f && collapse < 1f) {
                        val range = 140f
                        val next = ((collapse * range) - available.y).coerceIn(0f, range)
                        val consumed = next - collapse * range
                        collapse = next / range
                        return Offset(0f, -consumed)
                    }
                    return Offset.Zero
                }

                override fun onPostScroll(consumed: Offset, available: Offset, source: NestedScrollSource): Offset {
                    val atTop = listState.firstVisibleItemIndex == 0 && listState.firstVisibleItemScrollOffset == 0
                    if (available.y > 0f && collapse > 0f && atTop) {
                        val range = 140f
                        val next = ((collapse * range) - available.y).coerceIn(0f, range)
                        val consumedY = collapse * range - next
                        collapse = next / range
                        return Offset(0f, consumedY)
                    }
                    return Offset.Zero
                }
            }
        }
        val expanded = 1f - collapse

        Card(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 8.dp),
            colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant)
        ) {
            Column(modifier = Modifier.padding(horizontal = 12.dp, vertical = (8 + 4 * expanded).dp)) {
                Text(
                    text = if (latestCount != null) "Last Count: ${DateTimeUtils.formatCountTimestamp(latestCount!!.timestamp)}" else "No counts found",
                    style = if (collapse > 0.6f) MaterialTheme.typography.bodyMedium else MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    maxLines = 1
                )
                if (expanded > 0.05f) {
                    Column(
                        modifier = Modifier
                            .clipToBounds()
                            .graphicsLayer { alpha = expanded }
                            .heightIn(max = (56.dp * expanded))
                    ) {
                        if (latestCount?.lastAmended != null) {
                            Text("Amended: ${DateTimeUtils.formatCountTimestamp(latestCount!!.lastAmended)}", style = MaterialTheme.typography.bodySmall)
                        }
                        if (userRole == UserRole.ADMIN && latestCount != null) {
                            Text("Total Stock Value: £${formatDecimal(totalStockValuation)}", style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.primary)
                        }
                    }
                }
            }
        }

        Row(
            modifier = Modifier.fillMaxWidth().background(MaterialTheme.colorScheme.primary).padding(horizontal = 12.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text("Item Name", modifier = Modifier.weight(2f), color = MaterialTheme.colorScheme.onPrimary, fontWeight = FontWeight.Bold)
            Text("In Stock", modifier = Modifier.weight(1f), color = MaterialTheme.colorScheme.onPrimary, fontWeight = FontWeight.Bold)
            if (userRole == UserRole.ADMIN) {
                Text("Value (£)", modifier = Modifier.weight(1f), color = MaterialTheme.colorScheme.onPrimary, fontWeight = FontWeight.Bold, textAlign = androidx.compose.ui.text.style.TextAlign.End)
            }
        }

        if (isLoading) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(MaterialTheme.colorScheme.background)
                    .clickable(enabled = false) { },
                contentAlignment = Alignment.Center
            ) {
                BrandLoadingSpinner(Modifier.fillMaxSize())
            }
        } else {
            LazyColumn(
                state = listState,
                modifier = Modifier.fillMaxSize().nestedScroll(nestedScroll)
            ) {
                items(countDetails) { item ->
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 10.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = itemNames[item.itemId] ?: item.name ?: "Item #${item.itemId}",
                            modifier = Modifier.weight(2f)
                        )
                        Text(formatStockAmount(itemsMap[item.itemId], item.countedRealAmount), modifier = Modifier.weight(1f), fontWeight = FontWeight.Bold)
                        if (userRole == UserRole.ADMIN) {
                            val itemObj = itemsMap[item.itemId]
                            val lineValue = calculateItemCost(itemObj, item.countedRealAmount)
                            Text("£${formatDecimal(lineValue)}", modifier = Modifier.weight(1f), fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.primary, textAlign = androidx.compose.ui.text.style.TextAlign.End)
                        }
                    }
                    HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.1f))
                }
            }
        }
    }
}
